import React from 'react';
import { Marker, Tooltip, Popup, Polyline, Circle } from 'react-leaflet';
import L from 'leaflet';
import { dailyCombatSlantStatus, getCombatSlantStatusForDay, getCombatVehiclesForDay, getSecuritySectorCoverageForDay, readinessStatusColors, combatVehicleColors, weaponTypeInfo } from './combatSlantData';
import { timePointToDay } from './bulkFuelData';
import { convoyRouteSegments, alternativeRouteSegment } from './convoyData';
// Create custom icon for combat vehicles
const createCombatVehicleIcon = (type: string, position: string, isOperational: boolean, weapons: string[]) => {
  // Map type to icon and color
  const color = type === 'mrap' ? '#1E40AF' : '#7C3AED';
  const icon = type === 'mrap' ? '🛡️' : '🚙';
  const opacity = isOperational ? 1 : 0.5;
  // Create weapon indicators
  const weaponIcons = weapons.map(weapon => {
    if (weapon === 'm2') return '🔫';
    if (weapon === 'mk19') return '💥';
    if (weapon === 'm240') return '🔪';
    return '';
  }).join(' ');
  return L.divIcon({
    html: `
      <div style="display: flex; flex-direction: column; align-items: center; opacity: ${opacity}">
        <div style="display: flex; align-items: center; justify-content: center; background-color: white; width: 36px; height: 36px; border-radius: 50%; box-shadow: 0 2px 4px rgba(0,0,0,0.2); border: 3px solid ${color};">
          <div style="font-size: 18px;">${icon}</div>
        </div>
        <div style="background-color: ${color}; color: white; font-weight: bold; padding: 2px 6px; border-radius: 4px; font-size: 10px; margin-top: 2px;">
          ${position.toUpperCase()}
        </div>
        <div style="font-size: 14px; margin-top: 2px;">
          ${weaponIcons}
        </div>
      </div>
    `,
    className: '',
    iconSize: [36, 70],
    iconAnchor: [18, 40]
  });
};
// Create custom icon for combat slant status
const createCombatSlantStatusIcon = (day: number, combatPower: number, weaponsLoad: number, status: string) => {
  // Map status to color
  const color = status === 'green' ? '#059669' : status === 'amber' ? '#D97706' : '#DC2626';
  return L.divIcon({
    html: `
      <div style="display: flex; flex-direction: column; align-items: center; width: 120px;">
        <div style="background-color: white; padding: 6px; border-radius: 6px; box-shadow: 0 2px 4px rgba(0,0,0,0.1); width: 100%;">
          <div class="font-bold text-xs mb-1" style="font-weight: bold; font-size: 11px; margin-bottom: 4px; text-align: center;">COMBAT SLANT: DAY ${day}</div>
          <div style="display: flex; align-items: center; margin-bottom: 4px;">
            <div style="width: 70px; font-size: 10px;">Combat Power:</div>
            <div style="flex-grow: 1; background-color: #f3f4f6; height: 6px; border-radius: 3px; overflow: hidden;">
              <div style="background-color: ${color}; height: 100%; width: ${combatPower}%;"></div>
            </div>
            <div style="margin-left: 4px; font-size: 10px;">${combatPower}%</div>
          </div>
          <div style="display: flex; align-items: center;">
            <div style="width: 70px; font-size: 10px;">Weapons Load:</div>
            <div style="flex-grow: 1; background-color: #f3f4f6; height: 6px; border-radius: 3px; overflow: hidden;">
              <div style="background-color: ${color}; height: 100%; width: ${weaponsLoad}%;"></div>
            </div>
            <div style="margin-left: 4px; font-size: 10px;">${weaponsLoad}%</div>
          </div>
        </div>
        <div style="background-color: ${color}; color: white; font-weight: bold; padding: 2px 8px; border-radius: 4px; font-size: 10px; margin-top: 2px;">
          ${status === 'green' ? '🟢' : status === 'amber' ? '🟠' : '🔴'} ${status.toUpperCase()}
        </div>
      </div>
    `,
    className: '',
    iconSize: [120, 80],
    iconAnchor: [60, 40]
  });
};
interface CombatSlantLayerProps {
  selectedTimePoint: string;
  useAlternativeRoute?: boolean;
}
export const CombatSlantLayer: React.FC<CombatSlantLayerProps> = ({
  selectedTimePoint,
  useAlternativeRoute = false
}) => {
  // Convert selected time point to day number
  const currentDay = timePointToDay(selectedTimePoint);
  // Get the route segments based on the selected route
  const routeSegments = convoyRouteSegments.filter(segment => {
    // For day 1, check if we should use the alternative route
    if (segment.day === 1 && useAlternativeRoute) {
      return false;
    }
    return true;
  });
  // Add alternative route if selected
  const allSegments = useAlternativeRoute ? [...routeSegments.filter(segment => segment.day !== 1), alternativeRouteSegment] : routeSegments;
  // Sort segments by day
  const sortedSegments = allSegments.sort((a, b) => a.day - b.day);
  // Get combat slant status for current day
  const combatSlantStatus = getCombatSlantStatusForDay(currentDay);
  // Get combat vehicles for current day
  const combatVehicles = getCombatVehiclesForDay(currentDay);
  // Get security sector coverage for current day
  const securitySectors = getSecuritySectorCoverageForDay(currentDay);
  return <>
      {/* Render combat vehicles */}
      {currentDay > 0 && combatVehicles.map(vehicle => <Marker key={`combat-vehicle-${vehicle.id}`} position={vehicle.coordinates} icon={createCombatVehicleIcon(vehicle.type, vehicle.position, vehicle.isOperational, vehicle.weapons)} data-id="element-125">
            <Popup data-id="element-126">
              <div className="min-w-[200px]" data-id="element-127">
                <h3 className="font-bold text-base" data-id="element-128">{vehicle.name}</h3>
                <p className="text-sm mt-1" data-id="element-129">
                  <span className="font-medium" data-id="element-130">Type:</span>{' '}
                  {vehicle.type === 'mrap' ? 'MRAP' : 'Gun Truck'}
                </p>
                <p className="text-sm" data-id="element-131">
                  <span className="font-medium" data-id="element-132">Position:</span>{' '}
                  {vehicle.position.charAt(0).toUpperCase() + vehicle.position.slice(1)}
                </p>
                <p className="text-sm" data-id="element-133">
                  <span className="font-medium" data-id="element-134">Status:</span>{' '}
                  {vehicle.isOperational ? <span className="text-green-600" data-id="element-135">Operational</span> : <span className="text-red-600" data-id="element-136">Non-Operational</span>}
                </p>
                <div className="mt-2" data-id="element-137">
                  <h4 className="font-medium text-sm" data-id="element-138">Weapons:</h4>
                  <ul className="text-sm list-disc pl-5 mt-1" data-id="element-139">
                    {vehicle.weapons.map((weapon, index) => <li key={index} data-id="element-140">
                        {weaponTypeInfo[weapon as keyof typeof weaponTypeInfo]?.name || weapon}
                      </li>)}
                  </ul>
                </div>
              </div>
            </Popup>
          </Marker>)}
      {/* Render security sector coverage */}
      {currentDay > 0 && securitySectors.map(sector => {
      const vehicle = combatVehicles.find(v => v.id === sector.vehicleId);
      if (!vehicle || !vehicle.isOperational) return null;
      return <Circle key={`security-sector-${sector.id}`} center={sector.centerCoordinates} radius={sector.radiusMeters} pathOptions={{
        color: vehicle.type === 'mrap' ? combatVehicleColors.mrap : combatVehicleColors.gun_truck,
        fillColor: vehicle.type === 'mrap' ? combatVehicleColors.mrap : combatVehicleColors.gun_truck,
        fillOpacity: 0.1,
        weight: 1
      }} data-id="element-141">
              <Popup data-id="element-142">
                <div data-id="element-143">
                  <h3 className="font-bold" data-id="element-144">
                    Security Sector - {vehicle.name}
                  </h3>
                  <p className="text-sm mt-1" data-id="element-145">
                    Coverage angle: {sector.coverageAngle.start}° to{' '}
                    {sector.coverageAngle.end}°
                  </p>
                  <p className="text-sm" data-id="element-146">
                    Radius: {(sector.radiusMeters / 1000).toFixed(1)} km
                  </p>
                </div>
              </Popup>
            </Circle>;
    })}
      {/* Render combat slant status for each day */}
      {sortedSegments.map(segment => {
      const status = getCombatSlantStatusForDay(segment.day);
      // Get the coordinates for the marker - use the last point of each segment
      const markerPosition = segment.coordinates[segment.coordinates.length - 1];
      // Only show days up to the current selected day
      if (segment.day > currentDay && currentDay !== 0) {
        return null;
      }
      return <Marker key={`combat-slant-day-${segment.day}`} position={[markerPosition[0] + 0.1, markerPosition[1] - 0.1]} // Offset from other markers
      icon={createCombatSlantStatusIcon(segment.day, status.combatPowerPercentage, status.weaponsLoadPercentage, status.readinessStatus)} data-id="element-147">
            <Popup data-id="element-148">
              <div className="min-w-[250px]" data-id="element-149">
                <h3 className="font-bold text-base mb-2" data-id="element-150">
                  Combat Slant - Day {segment.day}
                </h3>
                <div className="space-y-2 text-sm" data-id="element-151">
                  <div data-id="element-152">
                    <span className="font-medium" data-id="element-153">Location: </span>
                    {segment.name.split(': ')[1]}
                  </div>
                  <div data-id="element-154">
                    <span className="font-medium" data-id="element-155">Combat Power: </span>
                    <span className={`font-medium ${status.readinessStatus === 'green' ? 'text-green-600' : status.readinessStatus === 'amber' ? 'text-amber-600' : 'text-red-600'}`} data-id="element-156">
                      {status.combatPowerPercentage}%
                    </span>
                  </div>
                  <div data-id="element-157">
                    <span className="font-medium" data-id="element-158">Security Vehicles: </span>
                    {status.securityVehicles.mraps} MRAPs +{' '}
                    {status.securityVehicles.gunTrucks} Gun Trucks
                  </div>
                  <div data-id="element-159">
                    <span className="font-medium" data-id="element-160">Weapons Load: </span>
                    <span className={`font-medium ${status.weaponsLoadPercentage >= 75 ? 'text-green-600' : status.weaponsLoadPercentage >= 50 ? 'text-amber-600' : 'text-red-600'}`} data-id="element-161">
                      {status.weaponsLoadPercentage}%
                    </span>
                  </div>
                  <div data-id="element-162">
                    <span className="font-medium" data-id="element-163">Status: </span>
                    <span className={`font-medium ${status.readinessStatus === 'green' ? 'text-green-600' : status.readinessStatus === 'amber' ? 'text-amber-600' : 'text-red-600'}`} data-id="element-164">
                      {status.readinessStatus === 'green' && '🟢 Green'}
                      {status.readinessStatus === 'amber' && '🟠 Amber'}
                      {status.readinessStatus === 'red' && '🔴 Red'}
                    </span>
                  </div>
                  <div data-id="element-165">
                    <span className="font-medium" data-id="element-166">Notes: </span>
                    {status.notes}
                  </div>
                  {segment.day === 5 && <div className="mt-1 p-2 bg-green-50 border border-green-200 rounded text-green-700" data-id="element-167">
                      <span className="font-medium" data-id="element-168">
                        Full Resupply Complete
                      </span>{' '}
                      - Combat power restored to 100%
                    </div>}
                </div>
              </div>
            </Popup>
          </Marker>;
    })}
      {/* Render a summary table when clicking on the route */}
      {sortedSegments.length > 0 && <Polyline positions={sortedSegments.flatMap(segment => segment.coordinates)} pathOptions={{
      color: 'transparent',
      weight: 20,
      opacity: 0.01
    }} data-id="element-169">
          <Popup data-id="element-170">
            <div className="min-w-[300px]" data-id="element-171">
              <h3 className="font-bold text-base mb-2" data-id="element-172">
                Combat Slant Summary - 7-Day View
              </h3>
              <table className="w-full text-sm border-collapse" data-id="element-173">
                <thead data-id="element-174">
                  <tr className="bg-gray-100" data-id="element-175">
                    <th className="border px-2 py-1 text-left" data-id="element-176">Day</th>
                    <th className="border px-2 py-1 text-right" data-id="element-177">
                      Combat Power
                    </th>
                    <th className="border px-2 py-1 text-left" data-id="element-178">Vehicles</th>
                    <th className="border px-2 py-1 text-right" data-id="element-179">Weapons</th>
                    <th className="border px-2 py-1 text-left" data-id="element-180">Notes</th>
                  </tr>
                </thead>
                <tbody data-id="element-181">
                  {dailyCombatSlantStatus.map(status => <tr key={`summary-day-${status.day}`} className={status.day === 5 ? 'bg-green-50' : ''} data-id="element-182">
                      <td className="border px-2 py-1" data-id="element-183">{status.day}</td>
                      <td className={`border px-2 py-1 text-right font-medium ${status.readinessStatus === 'green' ? 'text-green-600' : status.readinessStatus === 'amber' ? 'text-amber-600' : 'text-red-600'}`} data-id="element-184">
                        {status.combatPowerPercentage}%
                        {status.day === 5 && <span className="ml-1 text-xs text-green-600" data-id="element-185">
                            (Resupply)
                          </span>}
                      </td>
                      <td className="border px-2 py-1 text-xs" data-id="element-186">
                        {status.securityVehicles.mraps} MRAPs +{' '}
                        {status.securityVehicles.gunTrucks} GTs
                      </td>
                      <td className={`border px-2 py-1 text-right ${status.weaponsLoadPercentage >= 75 ? 'text-green-600' : status.weaponsLoadPercentage >= 50 ? 'text-amber-600' : 'text-red-600'}`} data-id="element-187">
                        {status.weaponsLoadPercentage}%
                      </td>
                      <td className="border px-2 py-1 text-xs" data-id="element-188">
                        {status.notes}
                      </td>
                    </tr>)}
                </tbody>
              </table>
              <div className="mt-3 text-xs text-gray-500" data-id="element-189">
                <div className="font-medium mb-1" data-id="element-190">Legend:</div>
                <div data-id="element-191">
                  <span className="inline-block w-3 h-3 rounded-full mr-1 bg-blue-600" data-id="element-192"></span>
                  <span data-id="element-193">MRAP - Armored personnel carrier</span>
                </div>
                <div data-id="element-194">
                  <span className="inline-block w-3 h-3 rounded-full mr-1 bg-purple-600" data-id="element-195"></span>
                  <span data-id="element-196">Gun Truck - Armed support vehicle</span>
                </div>
                <div className="mt-1" data-id="element-197">
                  <span data-id="element-198">🔫 M2 .50 Cal | 💥 Mk19 | 🔪 M240</span>
                </div>
              </div>
            </div>
          </Popup>
        </Polyline>}
    </>;
};